import * as XLSX from 'xlsx';
import format from 'date-fns/format';
import { sv } from 'date-fns/locale';

/**
 * Konverterar ett namn till filnamn-vänligt format
 * (små bokstäver, bindestreck istället för mellanslag)
 */
const toFilename = (name) => {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9åäö-]/g, '');
};

/**
 * Exporterar månatlig energirapport för en energimätare till Excel
 * 
 * @param {Object} options - Exportoptioner
 * @param {Object} options.meter - Mätarobjekt med id, name, identifier
 * @param {Array} options.data - Energidata med format [{ t: "2024-01", import: 123.45 }, ...]
 * @param {Date} options.fromDate - Startdatum för rapporten
 * @param {Date} options.toDate - Slutdatum för rapporten
 * @param {string} [options.filename] - Valfritt anpassat filnamn
 * @returns {void}
 */
export const exportMonthlyEnergyReport = ({ meter, data, fromDate, toDate, filename }) => {
  // Skapa arbetsbok
  const workbook = XLSX.utils.book_new();
  
  // Förbered header-information
  const meterName = meter?.name || meter?.identifier || 'Okänd mätare';
  const meterFilename = toFilename(meterName);
  const reportPeriod = `${format(fromDate, 'yyyy-MM-dd')} - ${format(toDate, 'yyyy-MM-dd')}`;
  
  // Skapa metadata-sektion
  const metadataRows = [
    ['Månatlig Energirapport'],
    [],
    ['Mätare:', meterName],
    ['Rapportperiod:', reportPeriod],
    ['Genererad:', format(new Date(), 'yyyy-MM-dd HH:mm')],
    [],
    [] // Tom rad före tabellen
  ];

  // Formatera energidata för tabellen
  const tableHeader = ['Månad', 'Energiförbrukning (kWh)'];
  
  const tableRows = data.map(row => {
    // Formatera månad till läsbart format (t.ex. "2024-01" -> "Januari 2024")
    const monthDate = new Date(row.t + '-01');
    const monthName = format(monthDate, 'MMMM yyyy', { locale: sv });
    const energy = typeof row.import === 'number' ? row.import.toFixed(2) : '-';
    
    return [monthName, parseFloat(energy) || 0];
  });

  // Beräkna total förbrukning
  const totalEnergy = data.reduce((sum, row) => sum + (row.import || 0), 0);
  const averageEnergy = data.length > 0 ? totalEnergy / data.length : 0;

  // Lägg till sammanfattning
  const summaryRows = [
    [],
    ['Summa:', totalEnergy.toFixed(2)],
    ['Genomsnitt/månad:', averageEnergy.toFixed(2)]
  ];

  // Kombinera alla rader
  const allRows = [
    ...metadataRows,
    tableHeader,
    ...tableRows,
    ...summaryRows
  ];

  // Skapa arbetsblad
  const worksheet = XLSX.utils.aoa_to_sheet(allRows);

  // Sätt kolumnbredder
  worksheet['!cols'] = [
    { wch: 25 }, // Kolumn A - Månad
    { wch: 25 }  // Kolumn B - Energiförbrukning
  ];

  // Lägg till arbetsbladet till arbetsboken
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Energirapport');

  // Generera filnamn
  const defaultFilename = `energirapport_${meterFilename}_${format(fromDate, 'yyyyMM')}-${format(toDate, 'yyyyMM')}.xlsx`;
  const outputFilename = filename || defaultFilename;

  // Exportera filen
  XLSX.writeFile(workbook, outputFilename);
};

/**
 * Exporterar detaljerad energirapport med daglig data till Excel
 * 
 * @param {Object} options - Exportoptioner
 * @param {Object} options.meter - Mätarobjekt
 * @param {Array} options.data - Energidata med format [{ t: "2024-01-15", import: 12.34 }, ...]
 * @param {Date} options.fromDate - Startdatum
 * @param {Date} options.toDate - Slutdatum
 * @param {string} options.interval - Intervall (hour, day, month, year)
 * @param {string} [options.filename] - Valfritt anpassat filnamn
 * @returns {void}
 */
export const exportEnergyReport = ({ meter, data, fromDate, toDate, interval = 'day', filename }) => {
  const workbook = XLSX.utils.book_new();
  
  const meterName = meter?.name || meter?.identifier || 'Okänd mätare';
  const meterFilename = toFilename(meterName);
  
  // Formatera intervallnamn
  const intervalNames = {
    hour: 'Timme',
    day: 'Dag',
    month: 'Månad',
    year: 'År'
  };
  
  const metadataRows = [
    ['Energirapport'],
    [],
    ['Mätare:', meterName],
    ['Period:', `${format(fromDate, 'yyyy-MM-dd')} - ${format(toDate, 'yyyy-MM-dd')}`],
    ['Intervall:', intervalNames[interval] || interval],
    ['Genererad:', format(new Date(), 'yyyy-MM-dd HH:mm')],
    [],
    []
  ];

  // Formatera datum baserat på intervall
  const formatDate = (t) => {
    const date = new Date(t);
    switch (interval) {
      case 'hour':
        return format(date, 'yyyy-MM-dd HH:mm');
      case 'day':
        return format(date, 'yyyy-MM-dd');
      case 'month':
        return format(new Date(t + '-01'), 'MMMM yyyy', { locale: sv });
      case 'year':
        return format(date, 'yyyy');
      default:
        return t;
    }
  };

  const tableHeader = ['Tidpunkt', 'Energiförbrukning (kWh)'];
  
  const tableRows = data.map(row => [
    formatDate(row.t),
    typeof row.import === 'number' ? parseFloat(row.import.toFixed(2)) : 0
  ]);

  const totalEnergy = data.reduce((sum, row) => sum + (row.import || 0), 0);
  const averageEnergy = data.length > 0 ? totalEnergy / data.length : 0;

  const summaryRows = [
    [],
    ['Total förbrukning (kWh):', totalEnergy.toFixed(2)],
    ['Genomsnitt per period (kWh):', averageEnergy.toFixed(2)]
  ];

  const allRows = [
    ...metadataRows,
    tableHeader,
    ...tableRows,
    ...summaryRows
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(allRows);
  
  worksheet['!cols'] = [
    { wch: 25 },
    { wch: 25 }
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Energirapport');

  const defaultFilename = `energirapport_${meterFilename}_${format(fromDate, 'yyyyMMdd')}-${format(toDate, 'yyyyMMdd')}.xlsx`;
  XLSX.writeFile(workbook, filename || defaultFilename);
};

export default {
  exportMonthlyEnergyReport,
  exportEnergyReport
};

